import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, MailCheck, Wand2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1704455306925-1401c3012117?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGluaWN8ZW58MHx8fHwxNzg1NDEwNTM4fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/magic-link", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the link. Try password login.");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (e, p) => {
    setMode("password");
    setEmail(e);
    setPassword(p);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--yc-bg)]">
      {/* Left: Form */}
      <div className="flex flex-col justify-between p-8 lg:p-14">
        <div className="flex items-center gap-3">
          <span className="brand-mark">YC</span>
          <div>
            <div className="font-heading font-bold text-lg text-[var(--yc-navy)]">Youngness Centre</div>
            <div className="label-eyebrow">by Dr. Awish</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="label-eyebrow mb-3">HR &amp; Recruitment Console</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[var(--yc-navy)] tracking-tight leading-[1.05]">
            Every hire.
            <br />
            Every review.
            <br />
            <span className="text-[var(--yc-emerald)]">In one command centre.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Sign in to your HR workspace to manage the recruitment pipeline, retention reviews,
            attrition analytics and compliance across all 4 branches.
          </p>

          {mode === "magic" ? (
            sent ? (
              <div className="mt-10 bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="magic-link-sent">
                <MailCheck className="h-6 w-6 mx-auto text-[var(--yc-emerald)]" />
                <div className="font-heading font-semibold text-[var(--yc-navy)] mt-3">Check your email</div>
                <p className="text-sm text-slate-600 mt-1">
                  We sent a sign-in link to <strong>{email}</strong>. Click it to enter your workspace — no password needed. The link expires in 15 minutes.
                </p>
                <button type="button" onClick={() => setSent(false)} className="text-xs text-[var(--yc-emerald)] hover:underline mt-3">Use a different email</button>
              </div>
            ) : (
              <form onSubmit={sendMagicLink} className="mt-10 space-y-4">
                <div>
                  <Label htmlFor="email" className="label-eyebrow">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    className="mt-2 h-11 bg-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="magic-link-button"
                  className="w-full h-11 rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)] text-white font-semibold tracking-tight"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-2" /> Email me a sign-in link</>}
                </Button>
                <p className="text-xs text-slate-500 text-center">No password or OTP — just click the link we email you.</p>
              </form>
            )
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4">
              <div>
                <Label htmlFor="email" className="label-eyebrow">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 h-11 bg-white"
                />
              </div>
              <div>
                <Label htmlFor="password" className="label-eyebrow">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-2 h-11 bg-white"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)] text-white font-semibold tracking-tight"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter workspace"}
              </Button>
            </form>
          )}

          <button
            type="button"
            onClick={() => { setMode(mode === "magic" ? "password" : "magic"); setSent(false); }}
            className="mt-4 text-sm text-[var(--yc-emerald)] hover:underline w-full text-center"
            data-testid="login-mode-toggle"
          >
            {mode === "magic" ? "Sign in with password instead" : "Sign in with email link instead"}
          </button>

          <div className="mt-10 border-t border-slate-200 pt-6">
            <div className="label-eyebrow mb-3">Quick demo access</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button
                type="button"
                onClick={() => quickFill("drawish@youngness.com", "Founder@2026")}
                className="text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[var(--yc-emerald)] hover:bg-emerald-50 transition-colors"
                data-testid="quick-login-founder"
              >
                <div className="font-medium text-[var(--yc-navy)]">Founder</div>
                <div className="text-slate-500 truncate">drawish@youngness.com</div>
              </button>
              <button
                type="button"
                onClick={() => quickFill("priya.hr@youngness.com", "Demo@2026")}
                className="text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[var(--yc-emerald)] hover:bg-emerald-50 transition-colors"
                data-testid="quick-login-hr"
              >
                <div className="font-medium text-[var(--yc-navy)]">HR Manager</div>
                <div className="text-slate-500 truncate">priya.hr@youngness.com</div>
              </button>
              <button
                type="button"
                onClick={() => quickFill("ravi.recruiter@youngness.com", "Demo@2026")}
                className="text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[var(--yc-emerald)] hover:bg-emerald-50 transition-colors"
                data-testid="quick-login-recruiter"
              >
                <div className="font-medium text-[var(--yc-navy)]">Recruiter</div>
                <div className="text-slate-500 truncate">ravi.recruiter@youngness.com</div>
              </button>
              <button
                type="button"
                onClick={() => quickFill("anil.saritavihar@youngness.com", "Demo@2026")}
                className="text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[var(--yc-emerald)] hover:bg-emerald-50 transition-colors"
                data-testid="quick-login-branch"
              >
                <div className="font-medium text-[var(--yc-navy)]">Branch Manager</div>
                <div className="text-slate-500 truncate">anil.saritavihar@youngness.com</div>
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" /> Encrypted session · Role-scoped access
        </div>
      </div>

      {/* Right: Visual */}
      <div className="hidden lg:block relative overflow-hidden border-l border-slate-200">
        <img src={HERO} alt="Clinic" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[var(--yc-navy)]/70" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="label-eyebrow text-[var(--yc-gold-soft)] mb-3">Multi-Branch · Multi-Role</div>
            <div className="font-heading text-3xl font-bold leading-tight">
              Sarita Vihar · Mathura · Jaipur · Institute
            </div>
            <p className="mt-3 text-slate-200 leading-relaxed">
              Live KPI scorecard, predictive attrition risk, and one-click monthly HR summaries powered by Claude Sonnet 4.5.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
